import React, { useEffect, useMemo, useState } from 'react';
import { RubyLoader } from '../../../components/RubyLoader';
import { RubyToken } from '../../../components/RubyToken';
import type { AiTabProps } from '../AiControlCenter';
import UsageTab from '../tabs/UsageTab';
import { WIRED_ACTION_IDS, effectiveMultiplier, modelDisplayName, pct } from './catalogModel';
import { creditsLabel, creditsToRubyTokens, formatRubyTokens, usd } from './units';
import { useRubyAiCatalog, useRubyAiUsage } from './useRubyAi';
import { Card, CardHeader, ErrorBanner } from './ui';

const noop = () => {};
const CAP = 5;

const ACTOR_LABELS: Record<string, string> = {
  tenant: 'Tenant runs (charged)',
  admin_test: 'Admin test runs',
  platform: 'Platform jobs (no tenant)',
  unspecified: 'Not attributed (legacy callers)',
};

const marginOf = (revenueUsd: number, costUsd: number): number | null =>
  revenueUsd > 0 ? 1 - costUsd / revenueUsd : costUsd > 0 ? -Infinity : null;

const marginText = (m: number | null) => (m === -Infinity ? 'loss' : pct(m));

/**
 * Usage & Margin (mockup board 4). Every run writes one spend event with
 * action, vibe, model, vendor units, our cost and Ruby Tokens charged; this
 * page is built from those rows (GET /agents/admin/ai/usage). Vendor spend
 * counts EVERY call — tenant, admin test, platform and unmapped — so it
 * reconciles to vendor invoices. The vendor spend pivot sits below it (cost
 * only — spend rows carry no charge for engine runs, final review #3).
 */
const UsageMarginTab: React.FC<AiTabProps> = (props) => {
  const { range, onHeaderStats } = props;
  const { usage, error, loading } = useRubyAiUsage(range);
  const { catalog } = useRubyAiCatalog();
  const [allTenants, setAllTenants] = useState(false);
  // "By vibe" follows one wired action at a time (every wired action, not just product descriptions).
  const [vibeActionId, setVibeActionId] = useState('text.product_description');
  const [allAdmins, setAllAdmins] = useState(false);

  const unit = usage?.creditUnitPriceUsd ?? 0.01;

  useEffect(() => {
    if (!usage) return;
    onHeaderStats([
      { label: 'Ruby Tokens charged', value: formatRubyTokens(usage.totals.rubyTokensCharged), tone: 'white' },
      { label: 'Paid to vendors', value: usd(usage.vendorSpend.totalCostUsd), tone: 'rose' },
      { label: 'Margin', value: usage.totals.marginPct == null ? '—' : `${usage.totals.marginPct}%`, tone: (usage.totals.marginPct ?? 0) >= 0 ? 'emerald' : 'rose' },
    ]);
  }, [usage, onHeaderStats]);

  const actionLabel = (id: string) =>
    id === 'unmapped' ? 'Unmapped AI calls' : catalog?.actions.find((a) => a.actionId === id)?.label ?? id;
  const vibeLabel = (id: string) =>
    id === 'base' ? 'Base (no vibe)' : catalog?.vibes.find((v) => v.vibeId === id)?.label ?? id;
  const modelLabel = (ref: string) => {
    const [p, m] = ref.split('/');
    return catalog ? modelDisplayName(catalog.models, p, m) : ref;
  };

  const actionRows = useMemo(() => {
    if (!usage) return [];
    const seen = new Set<string>();
    const rows: Array<{ id: string; runs: number | null; tokens: number | null; cost: number | null; margin: string; unmapped?: boolean }> = [];
    for (const r of usage.byAction) {
      seen.add(r.actionId);
      const unmapped = r.actionId === 'unmapped';
      rows.push({
        id: r.actionId, runs: r.runs, tokens: r.rubyTokens, cost: r.vendorCostUsd,
        margin: unmapped ? 'cost only' : marginText(marginOf(r.revenueUsd, r.vendorCostUsd)), unmapped,
      });
    }
    for (const a of catalog?.actions ?? []) {
      if (!seen.has(a.actionId)) rows.push({ id: a.actionId, runs: null, tokens: null, cost: null, margin: '—' });
    }
    return rows.sort((a, b) => Number(!!a.unmapped) - Number(!!b.unmapped) || (b.runs ?? -1) - (a.runs ?? -1));
  }, [usage, catalog]);

  const vibeBars = useMemo(() => {
    if (!usage) return [];
    const rows = usage.byVibe.filter((v) => v.actionId === vibeActionId);
    const action = catalog?.actions.find((a) => a.actionId === vibeActionId);
    const max = Math.max(1, ...rows.map((r) => r.runs));
    return rows.map((r) => {
      const vibe = catalog?.vibes.find((v) => v.vibeId === r.vibeId);
      const premium = !!action && !!vibe && effectiveMultiplier(vibe, action) > action.multiplier;
      return { ...r, width: Math.max(4, Math.round((r.runs / max) * 100)), premium };
    }).sort((a, b) => b.runs - a.runs);
  }, [usage, catalog, vibeActionId]);

  const vibeActionIds = useMemo(() => {
    const ids = new Set<string>(WIRED_ACTION_IDS);
    usage?.byVibe.forEach((v) => { if (v.actionId !== 'unmapped') ids.add(v.actionId); });
    return [...ids];
  }, [usage]);

  if (error && !usage) return <ErrorBanner title="Failed to load Usage & Margin" detail={error} />;
  if (!usage) return <RubyLoader variant="page" className="bg-white border border-stone-200 rounded-xl" />;

  const t = usage.totals;
  const tenants = [...usage.byTenant].sort((a, b) => b.rubyTokens - a.rubyTokens);
  const admins = [...usage.adminTests].sort((a, b) => b.vendorCostUsd - a.vendorCostUsd);
  const allowanceTok = creditsToRubyTokens(t.allowanceCredits);
  const purchasedTok = creditsToRubyTokens(t.purchasedCredits);

  return (
    <>
      <div className={`grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 relative ${loading ? 'opacity-70' : ''}`}>
        {loading && <RubyLoader variant="overlay" />}
        <Kpi label="Ruby Tokens charged" value={<span className="inline-flex items-center gap-2"><RubyToken size={22} />{formatRubyTokens(t.rubyTokensCharged)}</span>}
          hint={`${creditsLabel(t.credits)} = ${usd(t.revenueUsd)} · ${t.runs} runs`} />
        <Kpi label="Paid to AI vendors" value={usd(usage.vendorSpend.totalCostUsd)}
          hint="every call, incl. admin tests — reconciles to vendor invoices" />
        <Kpi label="Margin" value={t.marginPct == null ? '—' : `${t.marginPct}%`} tone={(t.marginPct ?? 0) < 0 ? 'rose' : 'emerald'}
          hint={`revenue ${usd(t.revenueUsd)} − cost ${usd(t.vendorCostUsd)} (tenant runs)`} />
        <Kpi label="From allowance vs purchased" value={`${formatRubyTokens(allowanceTok)} / ${formatRubyTokens(purchasedTok)}`}
          hint="free monthly allowance / top-up Ruby Tokens" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1.4fr_1fr] gap-4 items-start">
        <Card className="overflow-hidden">
          <CardHeader title="By action" />
          <table className="w-full text-sm">
            <thead><tr className="bg-stone-50 text-[11px] uppercase tracking-wide text-stone-600">
              <th className="text-left font-semibold px-4 py-2.5">Action</th>
              <th className="text-right font-semibold px-3 py-2.5">Runs</th>
              <th className="text-right font-semibold px-3 py-2.5">Ruby Tokens</th>
              <th className="text-right font-semibold px-3 py-2.5">Cost</th>
              <th className="text-right font-semibold px-4 py-2.5">Margin</th>
            </tr></thead>
            <tbody>
              {actionRows.map((r) => (
                <tr key={r.id} className={`border-t border-stone-100 ${r.unmapped ? 'bg-amber-50/60' : ''}`}>
                  <td className="px-4 py-2.5 font-medium text-stone-900">{actionLabel(r.id)}</td>
                  <td className="px-3 py-2.5 text-right">{r.runs ?? '—'}</td>
                  <td className="px-3 py-2.5 text-right">{r.tokens == null ? '—' : formatRubyTokens(r.tokens)}</td>
                  <td className="px-3 py-2.5 text-right">{r.cost == null ? '—' : usd(r.cost)}</td>
                  <td className={`px-4 py-2.5 text-right ${r.margin === 'loss' ? 'text-rose-700' : 'text-stone-700'}`}>{r.margin}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <Card>
          <CardHeader title={`By vibe — ${actionLabel(vibeActionId)}`} />
          {vibeActionIds.length > 1 && (
            <div className="px-4 pt-3 flex flex-wrap gap-1.5">
              {vibeActionIds.map((id) => {
                const on = id === vibeActionId;
                return (
                  <button key={id} type="button" onClick={() => setVibeActionId(id)} aria-pressed={on}
                    className={`min-h-[44px] px-3 rounded-full text-[12px] font-semibold border transition-colors ${on ? 'bg-[#DC143C] border-[#DC143C] text-white' : 'bg-white border-stone-200 text-stone-700 hover:border-stone-300'}`}>
                    {actionLabel(id)}
                  </button>
                );
              })}
            </div>
          )}
          <div className="p-4 space-y-2.5">
            {vibeBars.length === 0 && <p className="text-sm text-stone-600">No runs in this range.</p>}
            {vibeBars.map((v) => (
              <div key={v.vibeId}>
                <div className="flex justify-between text-[13px] mb-1">
                  <span className="font-medium text-stone-900">{vibeLabel(v.vibeId)}</span>
                  <span className="text-stone-600">{v.runs} run{v.runs === 1 ? '' : 's'} · {formatRubyTokens(v.rubyTokens)}</span>
                </div>
                <div className="h-2.5 rounded-full bg-stone-100 overflow-hidden">
                  <div className={`h-full rounded-full ${v.premium ? 'bg-[#DC143C]' : 'bg-stone-400'}`} style={{ width: `${v.width}%` }} />
                </div>
              </div>
            ))}
            <p className="text-[12px] text-stone-600 pt-1">Ruby bars = premium vibes (above the base multiplier).</p>
          </div>
        </Card>
      </div>

      <Card className="overflow-hidden">
        <CardHeader title="Who is using what" hint={`${tenants.length} tenant × action × vibe row${tenants.length === 1 ? '' : 's'}`} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead><tr className="bg-stone-50 text-[11px] uppercase tracking-wide text-stone-600">
              <th className="text-left font-semibold px-4 py-2.5">Tenant</th>
              <th className="text-left font-semibold px-3 py-2.5">Action</th>
              <th className="text-left font-semibold px-3 py-2.5">Vibe</th>
              <th className="text-left font-semibold px-3 py-2.5">Model</th>
              <th className="text-right font-semibold px-3 py-2.5">Runs</th>
              <th className="text-right font-semibold px-3 py-2.5">Ruby Tokens</th>
              <th className="text-right font-semibold px-4 py-2.5">Margin</th>
            </tr></thead>
            <tbody>
              {tenants.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-stone-600">No tenant runs in this range.</td></tr>}
              {(allTenants ? tenants : tenants.slice(0, CAP)).map((r, i) => {
                const m = marginOf(r.credits * unit, r.vendorCostUsd);
                return (
                  <tr key={`${r.tenantId}-${r.actionId}-${r.vibeId}-${i}`} className="border-t border-stone-100">
                    <td className="px-4 py-2.5"><span className="font-medium text-stone-900">{r.tenantName || r.tenantId}</span>
                      {r.tenantName && <span className="block font-mono text-[11px] text-stone-500">{r.tenantId}</span>}</td>
                    <td className="px-3 py-2.5">{actionLabel(r.actionId)}</td>
                    <td className="px-3 py-2.5">{vibeLabel(r.vibeId)}</td>
                    <td className="px-3 py-2.5">{(r.models?.length ? r.models : [r.model]).map(modelLabel).join(', ')}</td>
                    <td className="px-3 py-2.5 text-right">{r.runs}</td>
                    <td className="px-3 py-2.5 text-right">{formatRubyTokens(r.rubyTokens)}</td>
                    <td className={`px-4 py-2.5 text-right ${m === -Infinity || (m ?? 0) < 0 ? 'text-rose-700' : ''}`}>{marginText(m)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {tenants.length > CAP && (
          <button type="button" onClick={() => setAllTenants((x) => !x)} className="w-full min-h-[44px] text-sm font-semibold text-[#9F1239] border-t border-stone-100 hover:bg-rose-50">
            {allTenants ? 'Show top 5' : `Show all ${tenants.length}`}
          </button>
        )}
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
        <Card className="overflow-hidden">
          <CardHeader title="Admin test runs" hint="Not charged to tenants, but real money to vendors." />
          <table className="w-full text-sm">
            <thead><tr className="bg-stone-50 text-[11px] uppercase tracking-wide text-stone-600">
              <th className="text-left font-semibold px-4 py-2.5">Who</th>
              <th className="text-left font-semibold px-3 py-2.5">Action · vibe</th>
              <th className="text-right font-semibold px-3 py-2.5">Runs</th>
              <th className="text-right font-semibold px-4 py-2.5">Vendor cost</th>
            </tr></thead>
            <tbody>
              {admins.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-stone-600">No admin test runs in this range.</td></tr>}
              {(allAdmins ? admins : admins.slice(0, CAP)).map((r, i) => (
                <tr key={`${r.actorId}-${r.actionId}-${r.vibeId}-${i}`} className="border-t border-stone-100">
                  <td className="px-4 py-2.5 break-all">{r.actorId}</td>
                  <td className="px-3 py-2.5">{actionLabel(r.actionId)} · {vibeLabel(r.vibeId)}</td>
                  <td className="px-3 py-2.5 text-right">{r.runs}</td>
                  <td className="px-4 py-2.5 text-right">{usd(r.vendorCostUsd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {admins.length > CAP && (
            <button type="button" onClick={() => setAllAdmins((x) => !x)} className="w-full min-h-[44px] text-sm font-semibold text-[#9F1239] border-t border-stone-100 hover:bg-rose-50">
              {allAdmins ? 'Show top 5' : `Show all ${admins.length}`}
            </button>
          )}
        </Card>

        <Card>
          <CardHeader title="Vendor spend — where every dollar went" />
          <div className="p-4 space-y-3">
            <div className="border border-stone-200 rounded-xl px-3 py-1">
              {['tenant', 'admin_test', 'platform'].map((k) => {
                const r = usage.vendorSpend.byActorType.find((x) => x.actorType === k);
                return <SpendRow key={k} label={ACTOR_LABELS[k]} value={usd(r?.costUsd ?? 0)} hint={r ? `${r.calls} calls` : undefined} />;
              })}
              {usage.vendorSpend.byActorType.filter((x) => !['tenant', 'admin_test', 'platform'].includes(x.actorType)).map((r) => (
                <SpendRow key={r.actorType} label={ACTOR_LABELS[r.actorType] ?? r.actorType} value={usd(r.costUsd)} hint={`${r.calls} calls`} />
              ))}
              <SpendRow label="Unmapped AI calls (already counted above)" value={usd(usage.vendorSpend.unmappedCostUsd)} muted />
            </div>
            <div>
              <p className="text-[12px] uppercase tracking-wider text-stone-600 mb-1.5">Total by vendor</p>
              <div className="space-y-1.5">
                {usage.vendorSpend.byVendor.map((v) => (
                  <div key={v.vendor} className="flex items-center justify-between gap-3 text-sm">
                    <span className="capitalize text-stone-800">{v.vendor}
                      <span className="text-[12px] text-stone-500"> · {(v.providers ?? []).map((p) => p.providerId).join(', ')} · {v.calls} calls</span>
                    </span>
                    <span className="font-medium">{usd(v.costUsd)} <span className="text-[12px] text-stone-500">({v.sharePct}%)</span></span>
                  </div>
                ))}
                <div className="flex items-center justify-between text-sm font-bold border-t border-stone-200 pt-1.5">
                  <span>Total</span><span>{usd(usage.vendorSpend.totalCostUsd)}</span>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
      <p className="text-[12px] text-stone-600">
        Every run writes one spend event with action, vibe, model, units, our cost and Ruby Tokens charged — the whole page is built from those rows.
      </p>

      <Card className="p-4 md:p-5 space-y-4">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Vendor spend pivot</h2>
          <p className="text-[12px] text-stone-600">What the vendors billed us, by tenant, service, feature, role or model. For what tenants were charged and the margin per run, see the Ruby Ledger tab.</p>
        </div>
        <UsageTab {...props} onHeaderStats={noop} />
      </Card>
    </>
  );
};

const Kpi: React.FC<{ label: string; value: React.ReactNode; hint: string; tone?: 'rose' | 'emerald' }> = ({ label, value, hint, tone }) => (
  <Card className="p-4">
    <p className="text-[12px] text-stone-600">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${tone === 'rose' ? 'text-rose-700' : tone === 'emerald' ? 'text-emerald-700' : 'text-stone-900'}`}>{value}</p>
    <p className="text-[12px] text-stone-600 mt-1">{hint}</p>
  </Card>
);

const SpendRow: React.FC<{ label: string; value: string; hint?: string; muted?: boolean }> = ({ label, value, hint, muted }) => (
  <div className={`flex items-center justify-between gap-3 py-1.5 border-b border-stone-100 last:border-0 text-sm ${muted ? 'text-stone-500' : 'text-stone-800'}`}>
    <span>{label}{hint && <span className="text-[12px] text-stone-500"> · {hint}</span>}</span>
    <span className="font-medium">{value}</span>
  </div>
);

export default UsageMarginTab;
